import { useState } from 'react'
import { useMyFamily, useIncomingFamilyRequests, useApproveFamilyLink, useRejectFamilyLink, useRequestFamilyLink, useAddManualFamily, useRemoveFamily } from '@/hooks/useFamily'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { EmptyState } from '@/components/profile/EmptyState'
import { Users, Check, X, Plus, Trash2 } from 'lucide-react'
import type { FamilyRelationshipType } from '@/types/database'

const REL_LABELS: Record<string, string> = { father: 'Father', mother: 'Mother', spouse: 'Spouse', child: 'Child' }

export function FamilyPage() {
  const { data: family = [], isLoading } = useMyFamily()
  const { data: requests = [] } = useIncomingFamilyRequests()
  const approve = useApproveFamilyLink()
  const reject = useRejectFamilyLink()
  const requestLink = useRequestFamilyLink()
  const addManual = useAddManualFamily()
  const remove = useRemoveFamily()

  const [dialog, setDialog] = useState<{ type: FamilyRelationshipType; tab: 'link' | 'manual' } | null>(null)
  const [linkUsername, setLinkUsername] = useState('')
  const [linkMessage, setLinkMessage] = useState('')
  const [manualFirst, setManualFirst] = useState('')
  const [manualMiddle, setManualMiddle] = useState('')
  const [manualLast, setManualLast] = useState('')
  const [manualBirth, setManualBirth] = useState('')
  const [manualMobile, setManualMobile] = useState('')
  const [manualSameBarangay, setManualSameBarangay] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null)

  const hasFather = family.some((f) => f.relationship_type === 'father')
  const hasMother = family.some((f) => f.relationship_type === 'mother')
  const hasSpouse = family.some((f) => f.relationship_type === 'spouse')

  const resetDialog = () => { setDialog(null); setLinkUsername(''); setLinkMessage(''); setManualFirst(''); setManualMiddle(''); setManualLast(''); setManualBirth(''); setManualMobile(''); setManualSameBarangay(false) }

  const handleLink = () => {
    if (!dialog || !linkUsername.trim()) return
    requestLink.mutate({ username: linkUsername.trim(), type: dialog.type, message: linkMessage || undefined }, { onSuccess: resetDialog })
  }

  const handleManual = () => {
    if (!dialog || !manualFirst.trim() || !manualLast.trim()) return
    addManual.mutate({
      relationshipType: dialog.type, firstName: manualFirst.trim(), middleName: manualMiddle.trim() || undefined,
      lastName: manualLast.trim(), birthDate: manualBirth || undefined, mobile: manualMobile || undefined,
      sameBarangay: manualSameBarangay,
    }, { onSuccess: resetDialog })
  }

  if (isLoading) return <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />)}</div>

  const grouped = { father: family.filter((f) => f.relationship_type === 'father'), mother: family.filter((f) => f.relationship_type === 'mother'), spouse: family.filter((f) => f.relationship_type === 'spouse'), child: family.filter((f) => f.relationship_type === 'child') }

  return (
    <div className="space-y-4">
      {/* Incoming requests */}
      {requests.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader><CardTitle className="text-base text-amber-800">Incoming Requests</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-md border border-amber-200 bg-white p-3">
                <div className="text-sm">
                  <span className="font-medium">{r.from_resident?.first_name} {r.from_resident?.last_name}</span>
                  <span className="text-muted-foreground"> claims to be your {REL_LABELS[r.relationship_type]?.toLowerCase()}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => approve.mutate(r.id)} disabled={approve.isPending}><Check className="h-3 w-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => reject.mutate(r.id)} disabled={reject.isPending}><X className="h-3 w-3" /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* My Family */}
      <Card>
        <CardHeader><CardTitle className="text-base">My Family</CardTitle></CardHeader>
        <CardContent>
          {family.length === 0 ? (
            <EmptyState icon={Users} title="No family members yet" description="Add your family members to build your profile." />
          ) : (
            <div className="space-y-3">
              {(['father', 'mother', 'spouse', 'child'] as const).map((type) => {
                const members = grouped[type]
                if (members.length === 0) return null
                return (
                  <div key={type}>
                    <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">{type === 'child' ? 'Children' : REL_LABELS[type]}</p>
                    {members.map((m) => (
                      <div key={m.id} className="flex items-center justify-between rounded-md border p-3 mb-1">
                        <span className="text-sm font-medium">
                          {m.to_resident ? `${m.to_resident.first_name} ${m.to_resident.last_name}` : `${m.manual_first_name} ${m.manual_last_name}`}
                          {m.to_resident && <span className="ml-1 text-xs text-green-600">(linked)</span>}
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(m.id)}><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></Button>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add buttons */}
      <Card>
        <CardHeader><CardTitle className="text-base">Add Family Member</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {!hasFather && <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'father', tab: 'link' })}><Plus className="h-3 w-3" /> Father</Button>}
          {!hasMother && <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'mother', tab: 'link' })}><Plus className="h-3 w-3" /> Mother</Button>}
          {!hasSpouse && <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'spouse', tab: 'link' })}><Plus className="h-3 w-3" /> Spouse</Button>}
          <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'child', tab: 'link' })}><Plus className="h-3 w-3" /> Child</Button>
        </CardContent>
      </Card>

      {/* Add dialog */}
      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={resetDialog}>
          <div className="w-full max-w-sm rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-3 font-semibold">Add {REL_LABELS[dialog.type]}</h3>
            <div className="mb-4 flex gap-1">
              <button className={`flex-1 rounded-md py-1.5 text-xs font-medium ${dialog.tab === 'link' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`} onClick={() => setDialog({ ...dialog, tab: 'link' })}>Link BIMS User</button>
              <button className={`flex-1 rounded-md py-1.5 text-xs font-medium ${dialog.tab === 'manual' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`} onClick={() => setDialog({ ...dialog, tab: 'manual' })}>Manual Entry</button>
            </div>
            {dialog.tab === 'link' ? (
              <div className="space-y-3">
                <div className="space-y-1"><Label>Username</Label><Input value={linkUsername} onChange={(e) => setLinkUsername(e.target.value)} placeholder="e.g. juancruz" /></div>
                <div className="space-y-1"><Label>Message (optional)</Label><Input value={linkMessage} onChange={(e) => setLinkMessage(e.target.value)} /></div>
                <Button className="w-full" onClick={handleLink} disabled={requestLink.isPending || !linkUsername.trim()}>Send Link Request</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label>First Name *</Label><Input value={manualFirst} onChange={(e) => setManualFirst(e.target.value)} /></div>
                  <div className="space-y-1"><Label>Last Name *</Label><Input value={manualLast} onChange={(e) => setManualLast(e.target.value)} /></div>
                </div>
                <div className="space-y-1"><Label>Middle Name</Label><Input value={manualMiddle} onChange={(e) => setManualMiddle(e.target.value)} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label>Birth Date</Label><Input type="date" value={manualBirth} onChange={(e) => setManualBirth(e.target.value)} /></div>
                  <div className="space-y-1"><Label>Mobile</Label><Input value={manualMobile} onChange={(e) => setManualMobile(e.target.value)} /></div>
                </div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={manualSameBarangay} onChange={(e) => setManualSameBarangay(e.target.checked)} className="h-4 w-4 rounded border-input" /> Resides in same barangay</label>
                <Button className="w-full" onClick={handleManual} disabled={addManual.isPending || !manualFirst.trim() || !manualLast.trim()}>Save</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm remove dialog */}
      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmRemove(null)}>
          <div className="w-full max-w-sm rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-2 font-semibold">Remove family member?</h3>
            <p className="mb-1 text-sm text-muted-foreground">This removes the link from your profile.</p>
            <p className="mb-4 text-xs text-muted-foreground">Note: If this is a linked account, the corresponding link on the other person's profile is NOT auto-removed. They must remove it from their own account.</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmRemove(null)}>Cancel</Button>
              <Button variant="destructive" className="flex-1" onClick={() => { remove.mutate(confirmRemove); setConfirmRemove(null) }}>Remove</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
